import { useTheme } from "@emotion/react";
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Box, ButtonBase, InputBase, Popper } from "@mui/material";
import cx from 'classnames';
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import * as yup from "yup";
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import MAddInboundLine from "../../../../components/global/Modals/MAddInboundLine";
import MConfirmationWithValue from "../../../../components/global/Modals/MConfirmationWithValue";
import MConfirmDelete from "../../../../components/global/Modals/MConfirmDelete.jsx";
import MUpdateBatch from "../../../../components/global/Modals/MUpdateBatch.jsx";
import MUpdateLot from "../../../../components/global/Modals/MUpdateLot.jsx";
import Shbutton from "../../../../components/theme/buttons/Shbutton.jsx";
import ShSwitch from "../../../../components/theme/inputs/ShSwitch.jsx";
import SelectGroup from "../../../../components/theme/Select/SelectGroup.jsx";
import ShDateRangePicker from "../../../../components/theme/ShDateRangePicker.jsx";
import ShValidatedInputDropdown from "../../../../components/theme/dropdowns/ShValidatedInputDropdown.jsx";
import Heading from "../../../../components/theme/text/Heading.jsx";
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import Subheading from "../../../../components/theme/text/Subheading.jsx";
import Text from "../../../../components/theme/text/Text.jsx";
import { setWorkSpace } from "../../../../newapi/global/settingsSlice.js";
import { selectUserPermission } from "../../../../newapi/user/authentication/authSlice.js";
import { UserPrivileges } from "../../../../utils/permissions.js";
import { useCreateBatchMutation, useDeleteBatchMutation } from "../../../../newapi/inbound/batches.js";
import { usePreRegisterInboundLineMutation, useRegisterCountInboundLineMutation, useCompleteInboundMutation, useReceiveSerialNumberMutation } from "../../../../newapi/inbound/inboundsSlice.js";
import { useCreateLotMutation, useDeleteLotMutation } from "../../../../newapi/inbound/lots.js";
import { useCreateRepackingOrderMutation } from "../../../../newapi/inbound/repackingorders.js";
import { useUpdateProductMutation, useGetFilteredSuggestedLocationsQuery } from "../../../../newapi/warehouse/productSlice.js";
import { useFindWorkspacesSelectQuery, usePrintBarcodeMutation } from "../../../../newapi/warehouse/workspaceSlice.js";
import { themeSettings, tokens } from "../../../../theme.js";
import { showToastMessageDeleteRequest, showToastMessageRequestError, showToastMessageUpdateRequest } from "../../../../utils/toasts.js";
import { useGetCarrierForProcessingMutation, useGetCarriersSelectForProcessingMutation, useGetCarriersSelectQuery, useGetFilteredCarriersSelectQuery } from "../../../../newapi/carrier/carrierSlice.js";
import { ShDropdownQuery, DropdownBaseV2 } from "../../../../components/theme/dropdowns/Dropdown.jsx";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import TuneIcon from '@mui/icons-material/Tune';
import PopoverModal from "../../../../components/theme/Modal/PopoverModal.jsx";
import AMInboundProcessing from "../../../../components/global/Modals/ActionModal/AMInboundProcessing.jsx";
import MLinkCostToInbound from "../../../../components/global/Modals/MLinkCostToInbound.jsx";
import SidepanelLoader from "../../../../components/theme/sidepanel2/SidepanelLoader.js";
import { EditOutlined, LocalPrintshopOutlined, PrintOutlined } from "@mui/icons-material";
import MInboundEditAmountReceived from "../../../../components/global/Modals/MInboundEditAmountReceived.jsx";
import ShLabel from "../../../../components/theme/label/ShLabel.jsx";
import { getSerialNumberStateLabeldata, getWarehouseLocationLabelData } from "../../../../utils/labelColorText.js";
import { general_states } from "../../../../utils/staticEnums.js";
import MCreateSerialNumber from "../../../../components/global/Modals/MCreateSerialNumber.jsx";
import { AMInboundToProcess } from "../../../../components/global/Modals/ActionModal/AMInboundToProcess.jsx";
import MAddExtraSerialNumbers from "../../../../components/global/Modals/MAddExtraSerialNumbers.jsx";
import MCreateRepackOrder from "../../../../components/global/Modals/MCreateRepackOrder.jsx";
import MPrintSerialNumberBarcode from "../../../../components/global/Modals/MPrintSerialNumberBarcode.jsx";
import { useGetWarehouseZonesSelectQuery, useGetWarehousesSelectQuery } from "../../../../newapi/warehouse/warehouseApiSlice.js";
import { useGetWarehouseLocationTypesSelectQuery } from "../../../../newapi/warehouse/warehouseLocationTypeSlice.js";
import { useGetWarehouseLocationSelectQuery } from "../../../../newapi/warehouse/warehouselocationSlice.js";
import { useMoveStockMutation } from "../../../../newapi/warehouse/stockSlice.js";
import { CustomMyComponent } from "./InboundToProcessView.jsx";
// import SPStockDetails from "../../../../components/global/Sidepanels/SpStockDetails/SPStockDetails.jsx";

const combineLists = (existingLocations, availableLocations) => {
  const combinedMap = new Map();

  // Process existing locations (mark as not available)
  existingLocations.forEach((warehouse) => {
    const updatedLocations = warehouse?.locations?.map((location) => ({
      ...location,
      isAvailable: false,
    }));

    combinedMap.set(warehouse.id, {
      ...warehouse,
      locations: updatedLocations,
    });
  });

  // Process available locations (mark as available)
  availableLocations.forEach((warehouse) => {
    const updatedLocations = warehouse?.locations?.map((location) => ({
      ...location,
      isAvailable: true,
    }));

    if (combinedMap.has(warehouse.id)) {
      const existingWarehouse = combinedMap.get(warehouse.id);

      combinedMap.set(warehouse.id, {
        ...existingWarehouse,
        locations: [
          ...existingWarehouse.locations,
          ...updatedLocations,
        ],
      });
    } else {
      combinedMap.set(warehouse.id, {
        ...warehouse,
        locations: updatedLocations,
      });
    }
  });

  return Array.from(combinedMap.values());
}

export const PlacePanel = ({
    locations,
    showWarehouseSelector,
    selectedLocationWarehouseId,
    setSelectedLocationWarehouseId,
    warehouses,
    locationWarehouseSelected,
    locationFilters,
    setLocationFilters,
    lots,
    batches,
    flags,
    serialnumbers,
    inboundLineId,
    maxAmount,
    setHighlightColor,
    onPlaceSuccess,
    inboundLineToEdit,
    sx
}) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const snInputRef = useRef(null);
    const [moveStock, {isLoading: moveStockLoading}] = useMoveStockMutation();

    const [selectedCarrierName, setSelectedCarrierName] = useState(null);
    const [selectedLocationTypeName, setSelectedLocationTypeName] = useState(null);
    const [filterAnchorEl, setFilterAnchorEl] = useState(null);

    const activeFilterCount = (locationFilters?.zones?.length ?? 0)
        + (locationFilters?.carrierId ? 1 : 0)
        + (locationFilters?.warehouseLocationTypeId ? 1 : 0);

    const [selectedLotId, setSelectedLotId] = useState(null);
    const [selectedBatchId, setSelectedBatchId] = useState(null);
    const [selectedSerialNumbers, setSelectedSerialNumbers] = useState([]);
    const [isQuarantine, setIsQuarantine] = useState(false);

    const locationInputRef = useRef(null);
    const debounceRef = useRef(null);
    const [locationSearchValue, setLocationSearchValue] = useState('');
    const [debouncedLocationSearch, setDebouncedLocationSearch] = useState(null);
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [locationDropdownOpen, setLocationDropdownOpen] = useState(false);

    const {data: searchedLocations, isFetching: locationsFetching} = useGetWarehouseLocationSelectQuery(
        { searchString: debouncedLocationSearch },
        { skip: !debouncedLocationSearch || debouncedLocationSearch.length < 3 }
    );


    const handleLocationSearchChange = (e) => {
        const value = e.target.value;
        setLocationSearchValue(value);
        setLocationDropdownOpen(true);

        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }
        debounceRef.current = setTimeout(() => {
            setDebouncedLocationSearch(value);
        }, 300);
    };

    const handleLocationSelect = (location) => {
        setSelectedLocation(location);
        setLocationSearchValue(location.appName ?? '');
        setLocationDropdownOpen(false);
    };

    const handlePlace = () => {
        if (!selectedLocation || !inboundLineId) return;

        const amount = locationFilters.amount;
        if (!amount || amount <= 0) {
            toast.error(t("amount_required"), {
                position: "bottom-left",
                autoClose: 5000,
            });
            setHighlightColor("red");
            return;
        }

        if (amount > maxAmount) {
            toast.error(t("amount_exceeds_available"), {
                position: "bottom-left",
                autoClose: 5000,
            });
            setHighlightColor("red");
            return;
        }

        // Required batch/lot/serial numbers must be set, and selected batch/lot
        // must hold enough to cover the amount being placed. The offending
        // section is highlighted red in the panel below.
        if (batchInvalid || lotInvalid || serialNumbersInvalid) {
            setHighlightColor("red");
            return;
        }

        const moveBody = {
            inboundLineId: inboundLineId,
            amount: amount,
            newWarehouseLocation: selectedLocation.appName,
        };

        if (selectedLotId) {
            moveBody.lotId = selectedLotId;
        }
        if (selectedBatchId) {
            moveBody.batchId = selectedBatchId;
        }
        if (selectedSerialNumbers.length > 0) {
            moveBody.serialNumbers = selectedSerialNumbers.map(sn => sn.barcode);
        }
        if (isQuarantine) {
            moveBody.isQuarantine = true;
        }

        console.log(moveBody)

        moveStock(moveBody).unwrap()
            .then(res => {
                setHighlightColor("green");
                setSelectedLocation(null);
                setLocationSearchValue('');
                setSelectedLotId(null);
                setSelectedBatchId(null);
                setSelectedSerialNumbers([]);
                if (onPlaceSuccess) {
                    onPlaceSuccess();
                }
            })
            .catch(err => {
                showToastMessageRequestError(t('save_failed'), t, err);
                setHighlightColor("red");
            });
    };

    const canPlace = selectedLocation && locationFilters.amount > 0 && locationFilters.amount <= maxAmount;

    const receivedSerialNumbers = useMemo(() => {
        if (!serialnumbers || serialnumbers.length === 0) return [];
        return serialnumbers.filter(sn => sn.state === general_states.RECEIVED);
    }, [serialnumbers]);

    const handleSnScan = (e) => {
        if (e.key !== 'Enter') return;
        const inputValue = e.target.value.trim();
        if (!inputValue) return;

        const sn = receivedSerialNumbers.find(s => s.barcode === inputValue);
        if (sn && !selectedSerialNumbers.some(s => s.barcode === sn.barcode)) {
            setSelectedSerialNumbers(prev => [...prev, sn]);
        }
        e.target.value = "";
        snInputRef.current.focus();
    };

    const handleRemoveSerialNumber = (barcode) => {
        setSelectedSerialNumbers(prev => prev.filter(sn => sn.barcode !== barcode));
    };

    const lotOptions = useMemo(() => {
        if (!lots || lots.length === 0) return [];
        return lots.map(lot => ({
            ...lot,
            name: `${lot.number} (${lot.amountReceived})`,
        }));
    }, [lots]);

    const batchOptions = useMemo(() => {
        if (!batches || batches.length === 0) return [];
        return batches.map(batch => ({
            ...batch,
            name: `${batch.number} (${batch.amount})`,
        }));
    }, [batches]);

    const batchRequired = flags?.batchRequired ?? false;
    const lotRequired = flags?.lotRequired ?? false;
    const serialNumberRequired = flags?.serialNumberRequired ?? false;


    const selectedBatch = useMemo(() => batchOptions.find(b => b.id === selectedBatchId) ?? null, [batchOptions, selectedBatchId]);
    const selectedLot = useMemo(() => lotOptions.find(l => l.id === selectedLotId) ?? null, [lotOptions, selectedLotId]);

    // When a batch/lot is selected, its available amount must cover the amount being placed
    const batchAmountInsufficient = selectedBatch != null && locationFilters.amount > 0 && parseInt(selectedBatch.amount) < locationFilters.amount;
    const lotAmountInsufficient = selectedLot != null && locationFilters.amount > 0 && parseInt(selectedLot.amountReceived) < locationFilters.amount;

    const batchInvalid = (batchRequired && !selectedBatchId) || batchAmountInsufficient;
    const lotInvalid = (lotRequired && !selectedLotId) || lotAmountInsufficient;
    const serialNumbersInvalid = serialNumberRequired && selectedSerialNumbers.length !== locationFilters.amount;

    // Selecting a batch/lot sets the amount to place to that batch/lot's quantity
    const handleSelectBatch = (batch) => {
        setSelectedBatchId(batch ? batch.id : null);
        if (batch) {
            setLocationFilters(prev => ({...prev, amount: parseInt(batch.amount)}));
        }
    };

    const handleSelectLot = (lot) => {
        setSelectedLotId(lot ? lot.id : null);
        if (lot) {
            setLocationFilters(prev => ({...prev, amount: parseInt(lot.amountReceived)}));
        }
    };

    // When only a single batch/lot exists, select it immediately so the user doesn't have to
    useEffect(() => {
        if (batchOptions.length === 1 && !selectedBatchId) {
            setSelectedBatchId(batchOptions[0].id);
            setLocationFilters(prev => ({...prev, amount: parseInt(batchOptions[0].amount)}));
        }
    }, [batchOptions, selectedBatchId, setLocationFilters]);

    useEffect(() => {
        if (lotOptions.length === 1 && !selectedLotId) {
            setSelectedLotId(lotOptions[0].id);
            setLocationFilters(prev => ({...prev, amount: parseInt(lotOptions[0].amountReceived)}));
        }
    }, [lotOptions, selectedLotId, setLocationFilters]);

    const locationTypeOrder = { general: 0, bulk: 1 };

    const combinedList = useMemo(() => {
        if(!locations?.['availableLocations'] || !locations?.existingLocations) return [];

        const combined = combineLists(locations?.['availableLocations'],locations?.['existingLocations']);
        return combined.map(warehouse => ({
            ...warehouse,
            locations: [...warehouse.locations].sort((a, b) => {
                const orderA = locationTypeOrder[a.locationType] ?? 99;
                const orderB = locationTypeOrder[b.locationType] ?? 99;
                if (orderA !== orderB) return orderA - orderB;
                const stockA = a.stock > 0 ? 0 : 1;
                const stockB = b.stock > 0 ? 0 : 1;
                return stockA - stockB;
            }),
        }));
    },[locations]);

    return (
        <Box
            sx={{
                flex:1,
                display:"flex",
                flexDirection:'column',
                background:theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[100],
                borderRadius:4,
                p:3,
                mt:2,
                ...sx,
            }}
        >
            <Subheading>{t("place")}</Subheading>
            {showWarehouseSelector && (
                <ShDropdownQuery
                    name="name"
                    onChange={(selected, object) => {
                        setSelectedLocationWarehouseId(selected !== null && selected !== -1 ? selected : null);
                    }}
                    dataKey={"data"}
                    valueKey={"id"}
                    shComponent={<CustomMyComponent/>}
                    selected={selectedLocationWarehouseId ?? -1}
                    content={useGetWarehousesSelectQuery}
                    closeOnSelection
                >
                    {({anchorEl, onClick}) => (
                        <ButtonBase
                            onClick={onClick}
                            sx={{
                                height:32,
                                borderRadius:5,
                                background:theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                                display:"flex",
                                alignItems:'center',
                                px:0.5,
                                position:'relative',
                                "&:before" : {
                                    content:'""',
                                    position:'absolute',
                                    borderRadius:5,
                                    left:0,
                                    top:0,
                                    width:1,
                                    height:1,
                                    background:theme.palette.mode === "dark" ? colors.blue[100] : colors.primary[100],
                                    boxShadow:`0px 2px 4px ${colors.grey[200]}`,
                                }
                            }}
                        >
                            <Box
                                sx={{
                                    height:24,
                                    lineHeight:"24px",
                                    background:theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400],
                                    borderRadius:3,
                                    border: `1px solid ${theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400]}`,
                                    width:110,
                                    textAlign:"center",
                                    fontSize:14,
                                    fontWeight:"bold",
                                    color:theme.palette.mode === "dark" ? colors.txt["primaryDark"] : colors.txt["light"],
                                }}
                            >
                                {t("warehouse")}
                            </Box>
                            <Box sx={{ flex:1, ml:1.5, textAlign:"left" }}>
                                <Text bold>
                                    {warehouses.find(w => w.id === selectedLocationWarehouseId)?.name ?? ''}
                                </Text>
                            </Box>
                            <Box sx={{ display:'flex', gap:1.5, alignItems:'center' }}>
                                <Box
                                    sx={{
                                        height:24,
                                        width:24,
                                        borderRadius:3,
                                        display:"flex",
                                        alignItems:'center',
                                        justifyContent:"center",
                                        background: theme.palette.mode === "dark" ? colors.blue[100] : colors.primary[100],
                                        border: `1px solid ${theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400]}`,
                                        color: theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400],
                                    }}
                                >
                                    <KeyboardArrowDownIcon/>
                                </Box>
                            </Box>
                        </ButtonBase>
                    )}
                </ShDropdownQuery>
            )}
            {locationWarehouseSelected && (
                <>
                    <Box
                        sx={{
                            mt:1,
                            mb:1,
                            background: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[50],
                            p:2,
                            borderRadius:4,
                            display:"flex",
                            gap:4,
                            justifyContent:'space-between',
                            "@media screen and (max-width: 1500px)" : {
                                flexDirection:'column',
                                gap:2,
                            }
                        }}
                    >
                        <Box
                            sx={{
                                display:"flex",
                                flexDirection:'column',
                                "@media screen and (max-width: 1500px)" : {
                                    flexDirection:'row',
                                    justifyContent:"space-between"
                                }

                            }}
                        >
                            <Text>{t("to_place")}</Text>
                            <Subheading bold>{(inboundLineToEdit.amounts.received - inboundLineToEdit.amounts.moved)}</Subheading>
                        </Box>
                        <Box
                            sx={{
                                display:"flex",
                                gap:4,
                                "@media screen and (max-width: 1500px)" : {
                                    flexDirection:'column',
                                    gap:1,
                                }

                            }}
                        >
                            <Box
                                sx={{
                                    display:"flex",
                                    flexDirection:'column',
                                    "@media screen and (max-width: 1500px)" : {
                                        flexDirection:'row',
                                        justifyContent:"space-between"
                                    }

                                }}
                            >
                                <Text>{t("forecasted")}</Text>
                                <Text bold>{inboundLineToEdit.amounts.forecasted}</Text>
                            </Box>
                            <Box
                                sx={{
                                    display:"flex",
                                    flexDirection:'column',
                                    "@media screen and (max-width: 1500px)" : {
                                        flexDirection:'row',
                                        justifyContent:"space-between"
                                    }

                                }}
                            >
                                <Text>{t("to_receive")}</Text>
                                <Box
                                    sx={{
                                        display:"flex",
                                        color:colors.txt["primary"],
                                        gap:1.5,
                                    }}
                                >
                                    <Text bold>{(inboundLineToEdit.amounts.forecasted - inboundLineToEdit.amounts.received > 0 ? (inboundLineToEdit.amounts.forecasted - inboundLineToEdit.amounts.received) : 0)}</Text>
                                    
                                </Box>
                            </Box>
                            <Box
                                sx={{
                                    display:"flex",
                                    flexDirection:'column',
                                    "@media screen and (max-width: 1500px)" : {
                                        flexDirection:'row',
                                        justifyContent:"space-between"
                                    }

                                }}
                            >
                                <Text>{t("moved")}</Text>
                                <Text bold>{inboundLineToEdit.amounts.moved}</Text>
                            </Box>
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            mt:1,
                            display:"flex",
                            alignItems:'center',
                            gap:1,
                        }}
                    >
                        <Box
                            sx={{
                                flex:1,
                                display:"flex",
                                alignItems:'center',
                                background: colors.green[100],
                                border: `1px solid ${colors.green[400]}`,
                                borderRadius:4,
                                overflow:'hidden',
                                px:0.5,
                            }}
                        >
                            <Box
                                sx={{
                                    background: theme.palette.mode === "dark" ? colors.grey[800] : colors.grey[0],
                                    height:24,
                                    borderRadius:3,
                                    minWidth:80,
                                    textAlign:"center"
                                }}
                            >
                                <Text>{t("amount")}</Text>
                            </Box>
                            <InputBase
                                onChange={(e) => {
                                    const input = e.target.value;
                                    if (/^\d*$/.test(input)) {
                                        if (input === '') {
                                            setLocationFilters(prev => ({...prev, amount: null}));
                                        } else if (/^[1-9]\d*$/.test(input)) {
                                            setLocationFilters(prev => ({...prev, amount: parseInt(input)}));
                                        }
                                    }
                                }}
                                value={locationFilters.amount ?? ''}
                                sx={{
                                    width:1,
                                    height:32,
                                    px:1.5,
                                }}
                            />

                            <Box
                                sx={{
                                    mx:"-4px",
                                    my:"-4px",
                                    display:"flex",
                                    alignItems:'center',
                                    justifyContent:'space-between',
                                    background: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[0],
                                    borderRadius:4,
                                    height:32,
                                    px:0.5,
                                    gap:1,
                                }}
                            >
                                <Box
                                    sx={{
                                        background: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[0],
                                        height:24,
                                        lineHeight:"24px",
                                        borderRadius:3,
                                        px:1,
                                        textAlign:"center"
                                    }}
                                >
                                    <Text noBreak>{t("quarantine_stock")}</Text>
                                </Box>
                                <ShSwitch
                                    checked={isQuarantine}
                                    onChange={(e) => setIsQuarantine(e.target.checked)}
                                    sx={{
                                        marginRight:"-4px",
                                    }}
                                />
                            </Box>
                        </Box>

                        <ButtonBase
                            onClick={(e) => setFilterAnchorEl(e.currentTarget)}
                            sx={{
                                flexShrink:0,
                                height:32,
                                width:32,
                                borderRadius:4,
                                background: theme.palette.mode === "dark" ? colors.grey[800] : colors.grey[400],
                                color: theme.palette.mode === "dark" ? colors.txt["primary"] : colors.txt['light'],
                                position:'relative',
                            }}
                        >
                            <TuneIcon fontSize="small"/>
                            {activeFilterCount > 0 && (
                                <Box
                                    sx={{
                                        position:'absolute',
                                        top:-4,
                                        right:-4,
                                        height:16,
                                        minWidth:16,
                                        px:0.5,
                                        borderRadius:8,
                                        background: theme.palette.mode === "dark" ? colors.txt["primary"] : colors.txt['light'],
                                        color: theme.palette.mode === "dark" ? colors.txt["primaryDark"] : colors.grey[400],
                                        fontSize:10,
                                        fontWeight:'bold',
                                        display:'flex',
                                        alignItems:'center',
                                        justifyContent:'center',
                                    }}
                                >
                                    {activeFilterCount}
                                </Box>
                            )}
                        </ButtonBase>
                    </Box>
                    

                    {lotOptions.length > 0 && (
                        <DropdownBaseV2
                            displayName="name"
                            onChange={(selected) => {
                                const lot = lotOptions[selected];
                                handleSelectLot(lot);
                            }}
                            options={lotOptions}
                            closeOnSelection
                            selected={lotOptions.findIndex(l => l.id === selectedLotId)}
                        >
                            {({onClick}) => (
                                <ButtonBase
                                    onClick={onClick}
                                    sx={{
                                        mt:1,
                                        width:1,
                                        height:32,
                                        borderRadius:5,
                                        background:theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                                        display:"flex",
                                        alignItems:'center',
                                        px:0.5,
                                        position:'relative',
                                        "&:before" : {
                                            content:'""',
                                            position:'absolute',
                                            borderRadius:5,
                                            left:0,
                                            top:0,
                                            width:1,
                                            height:1,
                                            background:lotInvalid ? colors.red[100] : (theme.palette.mode === "dark" ? colors.blue[100] : colors.primary[100]),
                                            boxShadow:`0px 2px 4px ${colors.grey[200]}`,
                                        }
                                    }}
                                >
                                    <Box
                                        sx={{
                                            height:24,
                                            lineHeight:"24px",
                                            background:lotInvalid ? colors.red[400] : (theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400]),
                                            borderRadius:3,
                                            border: `1px solid ${lotInvalid ? colors.red[400] : colors.blue[400]}`,
                                            width:110,
                                            textAlign:"center",
                                            fontSize:14,
                                            fontWeight:"bold",
                                            color:theme.palette.mode === "dark" ? colors.txt["primaryDark"] : colors.txt["light"],
                                        }}
                                    >
                                        {t("lot")}
                                    </Box>
                                    <Box sx={{ flex:1, ml:1.5, textAlign:"left" }}>
                                        <Text bold>
                                            {lotOptions.find(l => l.id === selectedLotId)?.name ?? t("no_selection")}
                                        </Text>
                                    </Box>
                                    <Box
                                        sx={{
                                            height:24,
                                            width:24,
                                            borderRadius:3,
                                            display:"flex",
                                            alignItems:'center',
                                            justifyContent:"center",
                                            background: lotInvalid ? colors.red[100] : colors.blue[100],
                                            border: `1px solid ${lotInvalid ? colors.red[400] : colors.blue[400]}`,
                                            color: lotInvalid ? colors.red[400] : colors.blue[400],
                                        }}
                                    >
                                        <KeyboardArrowDownIcon/>
                                    </Box>
                                </ButtonBase>
                            )}
                        </DropdownBaseV2>
                    )}

                    {batchOptions.length > 0 && (
                        <DropdownBaseV2
                            displayName="name"
                            onChange={(selected) => {
                                const batch = batchOptions[selected];
                                handleSelectBatch(batch);
                            }}
                            options={batchOptions}
                            closeOnSelection
                            selected={batchOptions.findIndex(b => b.id === selectedBatchId)}
                        >
                            {({onClick}) => (
                                <ButtonBase
                                    onClick={onClick}
                                    sx={{
                                        mt:1,
                                        width:1,
                                        height:32,
                                        borderRadius:5,
                                        background:theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                                        display:"flex",
                                        alignItems:'center',
                                        px:0.5,
                                        position:'relative',
                                        "&:before" : {
                                            content:'""',
                                            position:'absolute',
                                            borderRadius:5,
                                            left:0,
                                            top:0,
                                            width:1,
                                            height:1,
                                            background:batchInvalid ? colors.red[100] : (theme.palette.mode === "dark" ? colors.orange[100] : colors.orange[100]),
                                            boxShadow:`0px 2px 4px ${colors.grey[200]}`,
                                        }
                                    }}
                                >
                                    <Box
                                        sx={{
                                            height:24,
                                            lineHeight:"24px",
                                            background: batchInvalid ? colors.red[400] : colors.orange[400],
                                            borderRadius:3,
                                            border: `1px solid ${batchInvalid ? colors.red[400] : colors.orange[400]}`,
                                            width:110,
                                            textAlign:"center",
                                            fontSize:14,
                                            fontWeight:"bold",
                                            color:theme.palette.mode === "dark" ? colors.txt["primaryDark"] : colors.txt["light"],
                                        }}
                                    >
                                        {t("batch")}
                                    </Box>
                                    <Box sx={{ flex:1, ml:1.5, textAlign:"left" }}>
                                        <Text bold>
                                            {batchOptions.find(b => b.id === selectedBatchId)?.name ?? t("no_selection")}
                                        </Text>
                                    </Box>
                                    <Box
                                        sx={{
                                            height:24,
                                            width:24,
                                            borderRadius:3,
                                            display:"flex",
                                            alignItems:'center',
                                            justifyContent:"center",
                                            background: batchInvalid ? colors.red[100] : colors.orange[100],
                                            border: `1px solid ${batchInvalid ? colors.red[400] : colors.orange[400]}`,
                                            color: batchInvalid ? colors.red[400] : colors.orange[400],
                                        }}
                                    >
                                        <KeyboardArrowDownIcon/>
                                    </Box>
                                </ButtonBase>
                            )}
                        </DropdownBaseV2>
                    )}

                    {receivedSerialNumbers.length > 0 && (
                        <Box
                            sx={{
                                background:theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
                                borderRadius:5,
                                border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                                boxShadow: theme.palette.mode === "dark" ? null : `0px 2px 4px ${colors.grey[200]}`,
                                mt:1.5,
                                display:"flex",
                                flexDirection:"column",
                                p:1.5,
                            }}
                        >
                            <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                                <Text bold>{t("serial_numbers")}</Text>
                                <Text bold sx={{ color: serialNumbersInvalid ? colors.red[400] : colors.green[400] }}>
                                    {`${selectedSerialNumbers.length}/${locationFilters.amount ?? 0}`}
                                </Text>
                            </Box>
                            <Box
                                sx={{
                                    mt:1,
                                    mb:2,
                                    display:'flex',
                                    alignItems:'center',
                                    px:0.5,
                                    height:32,
                                    background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[200],
                                    borderRadius:5,
                                    border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                                    boxShadow: theme.palette.mode === "dark" ? null : `0px 2px 4px ${colors.grey[200]}`,
                                }}
                            >
                                <img
                                    src="/img/barcode.svg"
                                    alt="Barcode"
                                    style={{
                                        width: '24px',
                                        marginLeft:"12px",
                                    }}
                                />
                                <InputBase
                                    inputRef={snInputRef}
                                    sx={{
                                        ml: 1.5,
                                        flex:1,
                                        color:colors.txt['primary'],
                                        fontSize:14,
                                        fontFamily:"Roboto,sans-serif"
                                    }}
                                    placeholder={t("scan_type_barcode")}
                                    onKeyDown={handleSnScan}
                                />
                                <DropdownBaseV2
                                    displayName="barcode"
                                    onChange={(selected) => {
                                        const sn = receivedSerialNumbers[selected];
                                        if (sn && !selectedSerialNumbers.some(s => s.barcode === sn.barcode)) {
                                            setSelectedSerialNumbers(prev => [...prev, sn]);
                                        }
                                    }}
                                    options={receivedSerialNumbers}
                                    closeOnSelection
                                    resetOnSelection
                                    selected={-1}
                                    searchOptions={{ enabled: true, placeholder: t("search"), searchables: ["barcode"] }}
                                >
                                    {({onClick}) => (
                                        <ButtonBase
                                            onClick={onClick}
                                            sx={{
                                                px:1.5,
                                                height:24,
                                                borderRadius:4,
                                                background:theme.palette.mode === "dark" ? colors.txt['secondary'] : colors.grey[400],
                                                color: theme.palette.mode === "dark" ? colors.txt['primary'] : colors.txt['light'],
                                                fontSize:14,
                                                fontWeight:"bold",
                                                fontFamily:"Roboto, sans-serif",
                                            }}
                                        >
                                            {t("choose")}
                                        </ButtonBase>
                                    )}
                                </DropdownBaseV2>
                            </Box>
                            
                            {selectedSerialNumbers.length > 0 && (
                                <Box
                                    sx={{
                                        display:"flex",
                                        flexDirection:"column",
                                        gap:1,
                                        maxHeight:120,
                                        overflow:'auto',
                                        overscrollBehavior:"contain",
                                        pb:0.5,
                                    
                                    }}
                                >
                                    {selectedSerialNumbers.map((sn) => (
                                        <Box
                                            key={sn.barcode}
                                            sx={{
                                                height:32,
                                                background: theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[0],
                                                borderRadius:4,
                                                pl:1.5,
                                                pr:0.5,
                                                display:'flex',
                                                alignItems:'center',
                                                justifyContent:'space-between',
                                                border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                                                boxShadow: theme.palette.mode === "dark" ? null : `0px 2px 4px ${colors.grey[200]}`,
                                            }}
                                        >
                                            <Text>{sn.barcode}</Text>
                                            <ButtonBase
                                                onClick={() => handleRemoveSerialNumber(sn.barcode)}
                                                sx={{
                                                    height:24,
                                                    width:24,
                                                    borderRadius:3,
                                                    color:colors.red[400],
                                                }}
                                            >
                                                <DeleteIcon fontSize="small"/>
                                            </ButtonBase>
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </Box>
                    )}

                    <PopoverModal
                        open={Boolean(filterAnchorEl)}
                        anchorEl={filterAnchorEl}
                        onClose={() => setFilterAnchorEl(null)}
                        disableEnforceFocus
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    >
                        <Box
                            sx={{
                                background: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
                                borderRadius:4,
                                p:2,
                                width:360,
                                display:'flex',
                                flexDirection:'column',
                                gap:1,
                            }}
                        >
                            <Text bold>{t("filter")}</Text>
                            <ShDropdownQuery
                                name="name"
                                onChange={(selected,object) => {
                                    const ids = Array.isArray(selected)
                                        ? selected.filter(id => id !== -1)
                                        : (selected !== null && selected !== -1 ? [selected] : []);
                                    setLocationFilters(prev => ({...prev, zones: ids}));
                                }}
                                dataKey={"data"}
                                valueKey={"id"}
                                shComponent={<CustomMyComponent/>}
                                selected={locationFilters?.zones}
                                multiple
                                content={useGetWarehouseZonesSelectQuery}
                                closeOnSelection
                                allowSelectAll
                            >
                            {({anchorEl,onClick}) => (
                                <ButtonBase
                                    onClick={onClick}
                                    sx={{
                                        flex:1,
                                        height:32,
                                        borderRadius:5,
                                        background:theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                                        display:"flex",
                                        alignItems:'center',
                                        px:0.5,
                                        position:'relative',
                                        "&:before" : {
                                            content:'""',
                                            position:'absolute',
                                            borderRadius:5,
                                            left:0,
                                            top:0,
                                            width:1,
                                            height:1,
                                            background:theme.palette.mode === "dark" ? colors.blue[100] : colors.primary[100],
                                            boxShadow:`0px 2px 4px ${colors.grey[200]}`,
                                        }
                                    }}
                                >
                                    <Box
                                        sx={{
                                            height:24,
                                            lineHeight:"24px",
                                            background:theme.palette.mode === "dark" ? colors.blue[400] :  colors.primary[400],
                                            borderRadius:3,
                                            border: `1px solid ${theme.palette.mode === "dark" ? colors.blue[400] :  colors.primary[400]}`,
                                            width:110,
                                            flexShrink:0,
                                            textAlign:"center",
                                            fontSize:14,
                                            fontWeight:"bold",
                                            color:theme.palette.mode === "dark" ? colors.txt["primaryDark"] : colors.txt["light"],
                                        }}
                                    >
                                        {t("zone")}
                                    </Box>
                                    <Box sx={{ flex:1, ml:1.5, textAlign:"left", overflow:"hidden" }}>
                                        <Text bold noBreak>
                                            {locationFilters.zones.length > 0
                                                ? `${locationFilters.zones.length} ${t("selected")}`
                                                : ''
                                            }
                                        </Text>
                                    </Box>
                                    <Box sx={{ display:'flex', gap:1.5, alignItems:'center' }}>
                                        <Box
                                            sx={{
                                                height:24,
                                                width:24,
                                                borderRadius:3,
                                                display:"flex",
                                                alignItems:'center',
                                                justifyContent:"center",
                                                background: theme.palette.mode === "dark" ? colors.blue[100] :  colors.primary[100],
                                                border: `1px solid ${ theme.palette.mode === "dark" ? colors.blue[400] :  colors.primary[400]}`,
                                                color: theme.palette.mode === "dark" ? colors.blue[400] :  colors.primary[400],
                                            }}
                                        >
                                            <KeyboardArrowDownIcon/>
                                        </Box>
                                    </Box>
                                </ButtonBase>
                            )}
                        </ShDropdownQuery>

                        <ShDropdownQuery
                            name="name"
                            onChange={(selected,object) => {
                                setLocationFilters(prev => ({
                                    ...prev,
                                    carrierId: selected !== null && selected !== -1 ? selected : null,
                                }));
                                setSelectedCarrierName(selected !== null && selected !== -1 ? (object?.name || object?.description) : null);
                            }}
                            config={"pallet"}
                            dataKey={null}
                            valueKey={"id"}
                            shComponent={<CustomMyComponent/>}
                            selected={locationFilters?.carrierId}
                            content={useGetFilteredCarriersSelectQuery}
                            closeOnSelection
                            allowSelectAll
                        >
                            {({anchorEl,onClick}) => (
                                <ButtonBase
                                    onClick={onClick}
                                    sx={{
                                        flex:1,
                                        height:32,
                                        borderRadius:5,
                                        background:theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                                        display:"flex",
                                        alignItems:'center',
                                        px:0.5,
                                        position:'relative',
                                        "&:before" : {
                                            content:'""',
                                            position:'absolute',
                                            borderRadius:5,
                                            left:0,
                                            top:0,
                                            width:1,
                                            height:1,
                                            background:theme.palette.mode === "dark" ? colors.blue[100] : colors.primary[100],
                                            boxShadow:`0px 2px 4px ${colors.grey[200]}`,
                                        }
                                    }}
                                >
                                    <Box
                                        sx={{
                                            height:24,
                                            lineHeight:"24px",
                                            background:theme.palette.mode === "dark" ? colors.blue[400] :  colors.primary[400],
                                            borderRadius:3,
                                            border: `1px solid ${theme.palette.mode === "dark" ? colors.blue[400] :  colors.primary[400]}`,
                                            width:110,
                                            flexShrink:0,
                                            textAlign:"center",
                                            fontSize:14,
                                            fontWeight:"bold",
                                            color:theme.palette.mode === "dark" ? colors.txt["primaryDark"] : colors.txt["light"],
                                        }}
                                    >
                                        {t("carrier")}
                                    </Box>
                                    <Box sx={{ flex:1, ml:1.5, textAlign:"left", overflow:"hidden" }}>
                                        <Text bold noBreak>{selectedCarrierName ?? ''}</Text>
                                    </Box>
                                    <Box sx={{ display:'flex', gap:1.5, alignItems:'center' }}>
                                        <Box
                                            sx={{
                                                height:24,
                                                width:24,
                                                borderRadius:3,
                                                display:"flex",
                                                alignItems:'center',
                                                justifyContent:"center",
                                                background: theme.palette.mode === "dark" ? colors.blue[100] :  colors.primary[100],
                                                border: `1px solid ${ theme.palette.mode === "dark" ? colors.blue[400] :  colors.primary[400]}`,
                                                color: theme.palette.mode === "dark" ? colors.blue[400] :  colors.primary[400],
                                            }}
                                        >
                                            <KeyboardArrowDownIcon/>
                                        </Box>
                                    </Box>
                                </ButtonBase>
                            )}
                        </ShDropdownQuery>

                        <ShDropdownQuery
                            name="name"
                            onChange={(selected,object) => {
                                setLocationFilters(prev => ({
                                    ...prev,
                                    warehouseLocationTypeId: selected !== null && selected !== -1 ? selected : null,
                                }));
                                setSelectedLocationTypeName(selected !== null && selected !== -1 ? (object?.name || object?.description) : null);
                            }}
                            dataKey={"data"}
                            valueKey={"id"}
                            shComponent={<CustomMyComponent/>}
                            selected={locationFilters.warehouseLocationTypeId}
                            content={useGetWarehouseLocationTypesSelectQuery}
                            closeOnSelection
                            allowSelectAll
                        >
                            {({anchorEl,onClick}) => (
                                <ButtonBase
                                    onClick={onClick}
                                    sx={{
                                        flex:1,
                                        height:32,
                                        borderRadius:5,
                                        background:theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                                        display:"flex",
                                        alignItems:'center',
                                        px:0.5,
                                        position:'relative',
                                        "&:before" : {
                                            content:'""',
                                            position:'absolute',
                                            borderRadius:5,
                                            left:0,
                                            top:0,
                                            width:1,
                                            height:1,
                                            background:theme.palette.mode === "dark" ? colors.blue[100] : colors.primary[100],
                                            boxShadow:`0px 2px 4px ${colors.grey[200]}`,
                                        }
                                    }}
                                >
                                    <Box
                                        sx={{
                                            height:24,
                                            lineHeight:"24px",
                                            background:theme.palette.mode === "dark" ? colors.blue[400] :  colors.primary[400],
                                            borderRadius:3,
                                            border: `1px solid ${theme.palette.mode === "dark" ? colors.blue[400] :  colors.primary[400]}`,
                                            width:110,
                                            flexShrink:0,
                                            textAlign:"center",
                                            fontSize:14,
                                            fontWeight:"bold",
                                            color:theme.palette.mode === "dark" ? colors.txt["primaryDark"] : colors.txt["light"],
                                        }}
                                    >
                                        {t("location_type")}
                                    </Box>
                                    <Box sx={{ flex:1, ml:1.5, textAlign:"left", overflow:"hidden" }}>
                                        <Text bold noBreak>{selectedLocationTypeName ?? ''}</Text>
                                    </Box>
                                    <Box sx={{ display:'flex', gap:1.5, alignItems:'center' }}>
                                        <Box
                                            sx={{
                                                height:24,
                                                width:24,
                                                borderRadius:3,
                                                display:"flex",
                                                alignItems:'center',
                                                justifyContent:"center",
                                                background: theme.palette.mode === "dark" ? colors.blue[100] :  colors.primary[100],
                                                border: `1px solid ${ theme.palette.mode === "dark" ? colors.blue[400] :  colors.primary[400]}`,
                                                color: theme.palette.mode === "dark" ? colors.blue[400] :  colors.primary[400],
                                            }}
                                        >
                                            <KeyboardArrowDownIcon/>
                                        </Box>
                                    </Box>
                                </ButtonBase>
                            )}
                        </ShDropdownQuery>
                        </Box>
                    </PopoverModal>

                    <Box
                        sx={{
                            mt:3,
                            background:theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[0],
                            borderRadius:3,
                            display:'flex',
                            flexDirection:'column',
                            gap:1,
                            p:1.5,
                        }}
                    >
                        <Subheading
                            sx={{
                            }}
                        >
                            {t("locations")}
                        </Subheading>

                        <Box
                            sx={{
                                maxHeight:200,
                                overflow:'auto',
                                overscrollBehavior:"contain",
                                display:'flex',
                                flexDirection:"column",
                                gap:3,
                            }}
                        >
                            {combinedList.map((warehouse,idx) => (
                                <Box 
                                    key={"warehouse"+warehouse.id}
                                    sx={{
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display:"inline-flex",
                                            alignItems:'center',
                                            height:24,
                                            borderRadius:3,
                                            px:1,
                                            background:warehouse.current ? theme.palette.mode === "dark" ? colors.blue[100] : colors.primary[100] : theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[200],
                                            color:warehouse.current ? theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400] : colors.txt['primary'],
                                            fontSize:14,
                                            fontFamily:"Roboto, sans-serif",
                                            fontWeight:"500",
                                            mb:1.5,
                                        }}
                                    >
                                        {warehouse.name}
                                    </Box>
                                    <Box
                                        sx={{
                                            display:"flex",
                                            flexDirection:"column",
                                            gap:1,
                                        }}
                                    >
                                        {warehouse.locations.map((location,idx) => {
                                            const {labeltext,labelcolor} = getWarehouseLocationLabelData(location.locationType,theme);
                                            return (
                                                <ButtonBase
                                                    key={"sug-loc-"+location.id}
                                                    onClick={() => handleLocationSelect({
                                                        id: location.id,
                                                        name: `${location.barcode} (${warehouse.name})`,
                                                        appName: location.barcode,
                                                    })}
                                                    className={cx('', {
                                                        'is-active': selectedLocation?.id === location.id,
                                                    })}
                                                    sx={{
                                                        borderRadius:4,
                                                        px:0.5,
                                                        display:'flex',
                                                        alignItems:'center',
                                                        justifyContent:"flex-start",
                                                        height:32,
                                                        background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[0],
                                                        border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                                                        boxShadow:theme.palette.mode === "dark" ? null :  `0px 2px 4px ${colors.grey[200]}`,
                                                        gap:1.5,
                                                        fontSize:14,
                                                        "&.is-active" : {
                                                            background:colors[labelcolor][100],
                                                            border: `1px solid ${colors[labelcolor][400]}`,
                                                        },
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            background:colors[labelcolor][100],
                                                            color:colors[labelcolor][400],
                                                            fontWeight:700,
                                                            fontFamily:"Roboto,sans-serif",
                                                            p:1.5,
                                                            height:24,
                                                            borderRadius:3,
                                                            display:'flex',
                                                            alignItems:'center',
                                                        }}
                                                    >
                                                        {t(labeltext)}
                                                    </Box>
                                                    <Text>
                                                        {location.barcode}
                                                    </Text>
                                                    
                                                    {location.stock > 0 ?
                                                    <Text
                                                        bold
                                                        sx={{
                                                            textAlign:"right",
                                                            pr:2,
                                                            width:80,
                                                            ml:"auto",
                                                        }}
                                                    >
                                                        {location.stock}
                                                    </Text>
                                                    :
                                                    <ShLabel
                                                        fitted
                                                        palette="normal"
                                                        borderless
                                                        size="24"
                                                        variant="green"
                                                        sx={{
                                                            ml:"auto",
                                                            borderRadius:3,
                                                        }}
                                                    >
                                                        {t("free")}
                                                    </ShLabel>
                                                    }
                                                </ButtonBase>
                                            )
                                        })}
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                        <Box
                            sx={{
                                mt:1,
                                position:'relative',
                            }}
                        >
                            <Box
                                sx={{
                                    display:'flex',
                                    height:32,
                                    background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[200],
                                    borderRadius:5,
                                    border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                                    boxShadow: theme.palette.mode === "dark" ? null : `0px 2px 4px ${colors.grey[200]}`,
                                    alignItems:'center',
                                    px:0.5,
                                }}
                            >
                                <Box
                                    sx={{
                                        background:theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[0],
                                        height:24,
                                        borderRadius:3,
                                        px:1.5,
                                        lineHeight:"24px",
                                    }}
                                >
                                    <Text>{t("selected_location")}</Text>
                                </Box>
                                <InputBase
                                    inputRef={locationInputRef}
                                    value={locationSearchValue}
                                    onChange={handleLocationSearchChange}
                                    onFocus={() => setLocationDropdownOpen(true)}
                                    onBlur={() => {
                                        setTimeout(() => setLocationDropdownOpen(false), 0);
                                    }}
                                    sx={{
                                        flex:1,
                                        height:32,
                                        px:1.5,
                                        fontSize:14,
                                    }}
                                    placeholder={t("scan_type_barcode")}
                                />
                                {selectedLocation && (
                                    <ButtonBase
                                        onMouseDown={(e) => e.preventDefault()}
                                        onClick={() => {
                                            setSelectedLocation(null);
                                            setLocationSearchValue('');
                                            setDebouncedLocationSearch(null);
                                            locationInputRef.current?.focus();
                                        }}
                                        sx={{
                                            height:24,
                                            width:24,
                                            borderRadius:3,
                                            color:colors.red[400],
                                        }}
                                    >
                                        <DeleteIcon fontSize="small"/>
                                    </ButtonBase>
                                )}
                            </Box>

                            {locationDropdownOpen && locationSearchValue.length >= 3 && (
                                <Box
                                    sx={{
                                        position:'absolute',
                                        left:0,
                                        right:0,
                                        top:'100%',
                                        mt:0.5,
                                        zIndex:10,
                                        background:theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
                                        border: theme.palette.mode === "dark" ? `1px solid ${colors.grey[400]}` : `1px solid ${colors.grey[100]}`,
                                        boxShadow:`0px 0px 19px rgba(49,65,95,.25)`,
                                        borderRadius:4,
                                        maxHeight:200,
                                        overflow:'auto',
                                        overscrollBehavior:"contain",
                                        p:1,
                                        display:'flex',
                                        flexDirection:'column',
                                        gap:0.5,
                                    }}
                                >
                                    {locationsFetching && (
                                        <Text light sx={{ p:1, textAlign:'center' }}>{t("loading")}</Text>
                                    )}
                                    {!locationsFetching && searchedLocations && searchedLocations.length > 0 && searchedLocations.map((location) => {
                                    
                                        return (
                                            <ButtonBase
                                                key={"loc-search-"+location.id}
                                                onMouseDown={(e) => e.preventDefault()}
                                                onClick={() => handleLocationSelect(location)}
                                                className={cx('', {
                                                    'is-active': selectedLocation?.id === location.id,
                                                })}
                                                sx={{
                                                    borderRadius:3,
                                                    height:24,
                                                    px:1,
                                                    pl:0.5,
                                                    display:"flex",
                                                    flexShrink:0,
                                                    alignItems:"center",
                                                    justifyContent:"flex-start",
                                                    fontSize:14,
                                                    fontFamily:"Roboto, sans-serif",
                                                    background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[0],
                                                    color:colors.txt["primary"],
                                                    gap:1,
                                                    "&.is-active" : {
                                                        background:theme.palette.mode === "dark" ? colors.blue[100] : colors.primary[100],
                                                        color:theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400],
                                                    },
                                                    "&:hover" : {
                                                        background:theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[100],
                                                    }
                                                }}
                                            >
                                                {location.appName}
                                            </ButtonBase>
                                        );
                                    })}
                                    {!locationsFetching && (!searchedLocations || searchedLocations.length === 0) && (
                                        <Text light sx={{ p:1, textAlign:'center' }}>{t("no_results")}</Text>
                                    )}

                                </Box>
                            )}
                        </Box>
                    </Box>

                    <Shbutton
                        disabled={!canPlace || moveStockLoading}
                        onClick={handlePlace}
                        isLoading={moveStockLoading}
                        block
                        size="32"
                        className={"group-btn"}
                        color={theme.palette.mode === "dark" ? "blue" : "primary"}
                        variant="contained"
                        sx={{
                            mt:2,
                        }}
                    >
                        {t("place")}
                    </Shbutton>
                </>
            )}
        </Box>
    );
};